import { useEffect, useMemo, useState } from "react";
import { api, HttpError } from "../api/client";
import type { ScheduleEvent } from "../api/types";

/**
 * Copy an Event onto other days.
 *
 * <p>Because an Event is always exactly one day (CONTEXT.md), a week-long
 * älgjakt is seven Events, and building it by hand is the same form seven
 * times. That is the job this does, and it is why the presets come first: the
 * common request is not "these particular Tuesdays", it is "and the rest of the
 * week". <b>Preset then confirm is two clicks</b>, which is the whole point —
 * the day grid underneath exists for the times the answer is not a preset, not
 * as the main path.
 *
 * <p>Presets set the selection rather than copying immediately. One extra click
 * buys you a look at exactly which seven days you are about to create, and
 * un-creating them is seven cancels.
 */

interface Props {
  event: ScheduleEvent;
  onCopied: (created: ScheduleEvent[]) => void;
  onClose: () => void;
}

/** How many days of choices the grid offers. Five weeks covers a season's planning. */
const GRID_DAYS = 35;

export function CopyEventDialog({ event, onCopied, onClose }: Props) {
  const source = parseLocalDate(event.date);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Every day the grid offers: the 35 after the source. The source itself is
  // excluded — it already exists, and the API drops it anyway.
  const days = useMemo(() => {
    const out: Date[] = [];
    for (let i = 1; i <= GRID_DAYS; i++) out.push(addDays(source, i));
    return out;
  }, [event.date]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * The presets. "Rest of the week" leads because it is the one people came
   * for — it completes the week the source day starts, Mon-first, and it is
   * the only preset whose size changes with the source (a Friday hunt gets
   * Sat+Sun, a Monday one gets six days).
   */
  const presets = useMemo(() => {
    const iso = (d: Date) => toISO(d);
    // Mon-first: Sun=0 becomes 6, so Monday is 0 days from the week's start.
    const dow = (source.getDay() + 6) % 7;
    const restOfWeek: string[] = [];
    for (let i = 1; i <= 6 - dow; i++) restOfWeek.push(iso(addDays(source, i)));

    const nextSix: string[] = [];
    for (let i = 1; i <= 6; i++) nextSix.push(iso(addDays(source, i)));

    const weekend: string[] = [];
    for (let i = 1; i <= 13; i++) {
      const d = addDays(source, i);
      const wd = d.getDay();
      if (wd === 6 || wd === 0) weekend.push(iso(d));
      if (weekend.length === 2) break;
    }

    const weekly: string[] = [];
    for (let w = 1; w <= 4; w++) weekly.push(iso(addDays(source, w * 7)));

    return [
      { key: "rest", label: "Rest of the week", dates: restOfWeek },
      { key: "six", label: "Next 6 days", dates: nextSix },
      { key: "weekend", label: "Next weekend", dates: weekend },
      { key: "weekly", label: "Weekly × 4", dates: weekly },
    ].filter((p) => p.dates.length > 0);
  }, [event.date]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggle(iso: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(iso)) next.delete(iso);
      else next.add(iso);
      return next;
    });
  }

  /** Re-clicking an active preset clears it, so it reads as a toggle. */
  function applyPreset(dates: string[]) {
    setSelected((prev) => {
      const already = dates.every((d) => prev.has(d)) && prev.size === dates.length;
      return already ? new Set() : new Set(dates);
    });
  }

  async function submit() {
    if (busy || selected.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      onCopied(await api.copyEvent(event.eventId, [...selected].sort()));
    } catch (e) {
      setError(
        e instanceof HttpError && e.status === 403
          ? "Copying an event is for hunting team members."
          : "Could not copy the event. Try again in a moment.",
      );
      setBusy(false);
    }
  }

  const count = selected.size;

  return (
    <div className="ev-overlay" role="presentation" onClick={onClose}>
      <div
        className="ev-dialog copy-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={`Copy ${event.title}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ev-dialog-hd">
          <h2 className="serif">Copy event</h2>
          <button type="button" className="ev-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <p className="copy-source">
          <span className="sched-kind">{event.kind}</span>
          <strong>{event.title}</strong>
          <span className="copy-source-date">
            {source.toLocaleDateString(undefined, {
              weekday: "long",
              day: "numeric",
              month: "long",
            })}
            {event.startTime && ` · ${event.startTime.slice(0, 5)}`}
          </span>
        </p>

        <div className="copy-presets" role="group" aria-label="Quick picks">
          {presets.map((p) => {
            const on = p.dates.every((d) => selected.has(d)) && selected.size === p.dates.length;
            return (
              <button
                key={p.key}
                type="button"
                className={`chip${on ? " active" : ""}`}
                aria-pressed={on}
                onClick={() => applyPreset(p.dates)}
              >
                {p.label}
                <span className="count">{p.dates.length}</span>
              </button>
            );
          })}
        </div>

        <div className="copy-grid-wrap">
          <div className="copy-grid">
            {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
              <div className="copy-grid-dow" key={i} aria-hidden="true">
                {d}
              </div>
            ))}
            {days.map((d, i) => {
              const iso = toISO(d);
              const on = selected.has(iso);
              const weekend = d.getDay() === 0 || d.getDay() === 6;
              return (
                <button
                  key={iso}
                  type="button"
                  className={`copy-day${on ? " on" : ""}${weekend ? " weekend" : ""}`}
                  // Offset only the first cell into its true weekday column
                  // (Mon-first); the rest flow from it, so every Monday lands
                  // in column 1 and a week reads as a row. Without this the
                  // grid starts flush left and the columns mean nothing.
                  style={i === 0 ? { gridColumnStart: ((d.getDay() + 6) % 7) + 1 } : undefined}
                  aria-pressed={on}
                  onClick={() => toggle(iso)}
                  aria-label={d.toDateString()}
                >
                  {/* The header row carries the weekday; repeating it in every
                      cell is noise at this size. The month shows on the 1st,
                      which is the only place the number alone is ambiguous. */}
                  <span className="copy-day-num">{d.getDate()}</span>
                  {d.getDate() === 1 && (
                    <span className="copy-day-mon">
                      {d.toLocaleDateString(undefined, { month: "short" })}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Said once, where it matters: people will assume answers follow. */}
        <p className="copy-note">
          Copies carry the kind, title, time, place and details — but not
          anyone's answer. Each new day starts unanswered.
        </p>

        {error && <p className="ev-error">{error}</p>}

        <div className="ev-actions">
          <button type="button" className="chip" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="chip active"
            disabled={busy || count === 0}
            onClick={() => void submit()}
          >
            {busy
              ? "Copying…"
              : count === 0
                ? "Pick some days"
                : `Copy to ${count} ${count === 1 ? "day" : "days"}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function addDays(d: Date, n: number): Date {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
}

function toISO(d: Date): string {
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
}

function parseLocalDate(s: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return new Date(s);
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}
